import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, Navigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { QueryErrorBanner, KpiStripSkeleton } from "../../components/feedback/QueryFeedback";
import KeyContactsPanel from "../../components/contacts/KeyContactsPanel";
import { WalkInPanel } from "../../components/events/WalkInPanel";
import type { WalkInPanelHandle } from "../../components/events/WalkInPanel";
import { contactsApi, eventsApi, ticketTiersApi } from "../../api";
import { rsvpApi } from "../../api/rsvp";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import { clearLastEventId, writeLastEventId, workspaceNav } from "../../routes/workspaceNavigation";
import { DashboardData } from "../../types";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { formatINR, formatPct, formatSellThroughPct } from "../../utils/format";
import { filterContactsForArtist } from "../../utils/contactProfile";
import "./DashboardPage.css";

function safePctDelta(current: number, baseline: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(baseline) || baseline === 0) return null;
  return ((current - baseline) / Math.abs(baseline)) * 100;
}

function formatSignedInr(n: number): string {
  if (!Number.isFinite(n) || n === 0) return formatINR(0);
  return `${n > 0 ? "+" : "−"}${formatINR(Math.abs(n))}`;
}




type DashboardView = "overview" | "tickets" | "pnl";

type Props = {
  workspace: WorkspaceId;
  embedMode?: boolean;
  view?: DashboardView;
};

const DashboardPage: React.FC<Props> = ({ workspace, embedMode = false, view }) => {
  const { eventId } = useParams();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [refreshingReview, setRefreshingReview] = useState(false);
  const [editingTierId, setEditingTierId] = useState<string | null>(null);
  const [editingTierSoldDraft, setEditingTierSoldDraft] = useState("");
  const [tiersExpanded, setTiersExpanded] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [ticketsSearch, setTicketsSearch] = useState("");
  const exportMenuRef = useRef<HTMLDivElement | null>(null);
  const walkInPanelRef = useRef<WalkInPanelHandle | null>(null);
  const eventsPath = workspaceNav.events(workspace);
  const contactsPath = workspaceNav.contacts(workspace);

  const invalidateAfterMutation = async () => {
    if (!eventId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.events.dashboard(workspace, eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.eventOs.all(workspace) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all(workspace) }),
    ]);
  };

  const updateTierMutation = useMutation({
    mutationFn: async (payload: { tierId: string; sold: number }) => {
      if (!eventId || !token) throw new Error("Missing event context");
      return ticketTiersApi.update(eventId, payload.tierId, { sold: payload.sold }, token);
    },
    onSuccess: async () => {
      toast.success("Tier sold count updated");
      setEditingTierId(null);
      setEditingTierSoldDraft("");
      await invalidateAfterMutation();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not update tier");
    },
  });


  useEffect(() => {
    if (eventId) {
      writeLastEventId(workspace, eventId);
    }
  }, [eventId, workspace]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!exportMenuRef.current) return;
      if (exportMenuRef.current.contains(event.target as Node)) return;
      setExportMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const dashboardQuery = useQuery({
    queryKey: eventId && token ? queryKeys.events.dashboard(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.dashboard(eventId!, token!),
    enabled: Boolean(token && eventId),
  });


  const contactsQuery = useQuery({
    queryKey: token ? queryKeys.contacts.list(workspace) : queryKeys.contacts.all(workspace),
    queryFn: () => contactsApi.list(token!),
    enabled: Boolean(token),
  });

  // If the dashboard request reports a 404, the sticky lastEventId is stale
  // (event deleted or no longer accessible). Drop it so the next Dashboard
  // tab click re-picks via the entry redirect instead of looping back here.
  useEffect(() => {
    if (!dashboardQuery.isError) return;
    const msg =
      dashboardQuery.error instanceof Error ? dashboardQuery.error.message : String(dashboardQuery.error ?? "");
    if (/404|not\s*found/i.test(msg)) {
      clearLastEventId(workspace);
    }
  }, [dashboardQuery.error, dashboardQuery.isError, workspace]);

  const data: DashboardData | undefined = dashboardQuery.data;
  const errorMsg =
    dashboardQuery.error instanceof Error ? dashboardQuery.error.message : dashboardQuery.error ? String(dashboardQuery.error) : "";

  const eventContacts = useMemo(
    () => filterContactsForArtist(contactsQuery.data ?? [], data?.event.artist_id),
    [contactsQuery.data, data?.event.artist_id],
  );

  useEffect(() => {
    if (!eventId || !data) return;
    rsvpApi.rememberEventMeta(eventId, {
      name: data.event.name,
      event_date: data.event.event_date,
      city: data.event.city,
      artist: data.event.artist,
      ticket_tiers: (data.ticket_summary.tiers ?? []).map((t) => ({
        id: t.tier_id,
        name: t.name,
        price: t.price,
      })),
    });
  }, [eventId, data]);



  const tierRows = useMemo(() => {
    if (!data) return [] as Array<{
      tier_id: string;
      name: string;
      price: number;
      quantity: number;
      sold: number;
      revenue: number;
      soldPct: number;
    }>;
    return data.ticket_summary.tiers.map((t) => {
      const soldPct = t.quantity ? (t.sold / t.quantity) * 100 : 0;
      return { ...t, soldPct };
    });
  }, [data]);

  const filteredTierRows = useMemo(() => {
    const q = ticketsSearch.trim().toLowerCase();
    if (!q) return tierRows;
    return tierRows.filter((t) => t.name.toLowerCase().includes(q));
  }, [tierRows, ticketsSearch]);

  if (!eventId) {
    return <Navigate to={eventsPath} replace />;
  }

  if (dashboardQuery.isPending) {
    return (
      <div className="app-shell-dash dashboard-page">
        <h1 className="app-shell-page-title">Event dashboard</h1>
        <p className="app-shell-lede dashboard-page__loading-lede">
          Loading financial + inventory slices for this event…
        </p>
        <KpiStripSkeleton count={6} />
      </div>
    );
  }

  if (dashboardQuery.isError) {
    return (
      <div className="app-shell-dash dashboard-page">
        <h1 className="app-shell-page-title">Event dashboard</h1>
        <QueryErrorBanner
          title="Dashboard request failed"
          message={errorMsg || "Unknown error"}
          onRetry={() => void dashboardQuery.refetch()}
          retrying={dashboardQuery.isFetching}
        />
        <Link to={eventsPath} className="app-shell-back-link dashboard-page__back-link--error">
          ← Back to events
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-shell-dash dashboard-page">
        <h1 className="app-shell-page-title">Event dashboard</h1>
        <div className="eventos-empty">No dashboard payload for this id.</div>
        <Link to={eventsPath} className="app-shell-back-link dashboard-page__back-link--empty">
          ← Back to events
        </Link>
      </div>
    );
  }

  const sellThrough = Number(data.ticket_summary.sell_through_pct) || 0;

  const review = data.review;
  const projectedRevenue = Number(data.metrics.projected_revenue) || 0;
  const totalCost = Number(data.metrics.total_cost) || 0;
  const projectedProfit = Number(data.metrics.profit ?? projectedRevenue - totalCost) || 0;
  const actualRevenue = Number(
    review?.total_revenue ?? data.metrics.actual_revenue ?? data.ticket_summary.revenue_generated ?? 0,
  );
  const actualProfit = Number(
    review?.net_profit ?? data.metrics.actual_profit ?? (actualRevenue - totalCost),
  );
  const ticketsSoldModel = Number(data.metrics.tickets_sold || 0);
  const projectedVsActualRows = [
    {
      label: "Revenue",
      projected: projectedRevenue,
      actual: actualRevenue,
      delta: actualRevenue - projectedRevenue,
      deltaPct: safePctDelta(actualRevenue, projectedRevenue),
    },
    {
      label: "Costs",
      projected: totalCost,
      actual: totalCost,
      delta: 0,
      deltaPct: 0,
    },
    {
      label: "Profit",
      projected: projectedProfit,
      actual: actualProfit,
      delta: actualProfit - projectedProfit,
      deltaPct: safePctDelta(actualProfit, projectedProfit),
    },
  ];




  const handleRefreshReview = async () => {
    if (!eventId || !token) return;
    setRefreshingReview(true);
    try {
      await eventsApi.refreshReview(eventId, token);
      await queryClient.invalidateQueries({ queryKey: queryKeys.events.dashboard(workspace, eventId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.eventOs.all(workspace) });
      await dashboardQuery.refetch();
      toast.success("Review refreshed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to refresh review");
    } finally {
      setRefreshingReview(false);
    }
  };

  const beginTierEdit = (tierId: string, currentSold: number) => {
    setEditingTierId(tierId);
    setEditingTierSoldDraft(String(currentSold));
  };

  const submitTierEdit = (tierId: string) => {
    const next = Number(editingTierSoldDraft);
    if (!Number.isFinite(next) || next < 0) {
      toast.error("Enter a non-negative number");
      return;
    }
    updateTierMutation.mutate({ tierId, sold: Math.floor(next) });
  };



  const exportPnlExcel = () => {
    const rows = [
      ["Event", data.event.name],
      ["City", data.event.city],
      ["Date", data.event.event_date],
      [],
      ["Metric", "Projected", "Actual", "Delta"],
      ...projectedVsActualRows.map((row) => [
        row.label,
        row.projected.toFixed(2),
        row.actual.toFixed(2),
        row.delta.toFixed(2),
      ]),
      [],
      ["Tier", "Price", "Qty", "Sold", "Revenue"],
      ...tierRows.map((tier) => [
        tier.name,
        tier.price.toFixed(2),
        String(tier.quantity),
        String(tier.sold),
        tier.revenue.toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.event.name.replace(/[^\w-]+/g, "_")}_pnl.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
  };

  const exportPnlPdf = () => {
    const wnd = window.open("", "_blank", "noopener,noreferrer,width=980,height=760");
    if (!wnd) {
      toast.error("Pop-up blocked. Allow pop-ups to export PDF.");
      return;
    }
    wnd.document.write(`
      <html><head><title>${data.event.name} - P&L</title>
      <style>body{font-family:"Outfit", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;padding:24px;color:#111}h1{margin:0 0 8px}table{width:100%;border-collapse:collapse;margin-top:14px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f4f4f4}</style>
      </head><body>
      <h1>${data.event.name}</h1>
      <div>${data.event.city} · ${data.event.event_date}</div>
      <table><thead><tr><th>Metric</th><th>Projected</th><th>Actual</th><th>Delta</th></tr></thead><tbody>
      ${projectedVsActualRows
        .map(
          (r) =>
            `<tr><td>${r.label}</td><td>${formatINR(r.projected)}</td><td>${formatINR(r.actual)}</td><td>${formatSignedInr(r.delta)}</td></tr>`,
        )
        .join("")}
      </tbody></table>
      </body></html>
    `);
    wnd.document.close();
    wnd.focus();
    wnd.print();
    setExportMenuOpen(false);
  };

  // Sprint 14 split Overview and P&L into their own pages
  // (EventOverviewPage / EventPnlPage). This component now serves the
  // Tickets tab only; ``view`` is retained so the route stays explicit.
  const pageView: DashboardView | "all" = embedMode && view ? view : "all";
  const showTickets = pageView === "all" || pageView === "tickets";

  return (
    <div className={`app-shell-dash dashboard-page${pageView === "tickets" ? " dashboard-page--tickets-fit" : ""}`}>
      {!embedMode ? (
      <>
      <div className="app-shell-dash-toolbar">
        <div>
          <div className="app-shell-refresh-row dashboard-page__toolbar-row">
            <Link to={eventsPath} className="app-shell-back-link dashboard-page__toolbar-back-link">
              ← Events
            </Link>
            <button type="button" className="eventos-nav-btn" onClick={() => void dashboardQuery.refetch()} disabled={dashboardQuery.isFetching}>
              {dashboardQuery.isFetching ? "Refreshing…" : "Refresh metrics"}
            </button>
          </div>
          <h1 className="app-shell-page-title dashboard-page__title">
            {data.event.name}
          </h1>
          <p className="app-shell-lede dashboard-page__lede">
            {data.event.city} · {data.event.event_date} · capacity {data.event.venue_capacity?.toLocaleString("en-IN") ?? "—"} ·{" "}
            <span className="app-shell-pill dashboard-page__status-pill">
              {data.event.status}
            </span>
          </p>
          <div className="app-shell-dash-toolbar-meta">
            <span className="app-shell-pill app-shell-pill--live">Financial core</span>
            <span className="app-shell-pill app-shell-pill--accent">Sell-through {formatSellThroughPct(sellThrough, ticketsSoldModel)}</span>
          </div>
        </div>
        <div className="dashboard-page__toolbar-actions">
          <div className="dashboard-page__export-menu" ref={exportMenuRef}>
            <button
              type="button"
              className="eventos-nav-btn"
              onClick={() => setExportMenuOpen((prev) => !prev)}
              aria-expanded={exportMenuOpen}
              aria-haspopup="menu"
            >
              Export P&L ▾
            </button>
            {exportMenuOpen ? (
              <div className="dashboard-page__export-dropdown" role="menu">
                <button type="button" className="dashboard-page__export-item" onClick={exportPnlPdf}>
                  Export PDF
                </button>
                <button type="button" className="dashboard-page__export-item" onClick={exportPnlExcel}>
                  Export Excel
                </button>
              </div>
            ) : null}
          </div>
          <Link to={workspaceNav.editEvent(workspace, eventId)} className="eventos-nav-btn dashboard-page__edit-link">
            Edit event
          </Link>
          <Link to={workspaceNav.commandCenter(workspace, eventId)} className="eventos-primary-btn dashboard-page__workspace-link">
            Open command center →
          </Link>
        </div>
      </div>

      <div className="dashboard-page__secondary-actions">
        <button
          type="button"
          className="dashboard-page__action-btn dashboard-page__action-btn--review"
          onClick={() => void handleRefreshReview()}
          disabled={refreshingReview}
        >
          <span className="dashboard-page__action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
          </span>
          <span className="dashboard-page__action-copy">
            <span className="dashboard-page__action-title">
              {refreshingReview ? "Refreshing review…" : "Refresh review"}
            </span>
            <span className="dashboard-page__action-desc">Recalculate financial review snapshot</span>
          </span>
        </button>
        <Link to={workspaceNav.logistics(workspace, eventId)} className="dashboard-page__action-btn dashboard-page__action-btn--logistics">
          <span className="dashboard-page__action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
              <path d="M15 18H9" />
              <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
              <circle cx="17" cy="18" r="2" />
              <circle cx="7" cy="18" r="2" />
            </svg>
          </span>
          <span className="dashboard-page__action-copy">
            <span className="dashboard-page__action-title">Manage logistics</span>
            <span className="dashboard-page__action-desc">Budgets, tasks, and run-of-show</span>
          </span>
        </Link>
        <Link to={workspaceNav.invoices(workspace, eventId)} className="dashboard-page__action-btn dashboard-page__action-btn--invoice">
          <span className="dashboard-page__action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
              <path d="M8 13h8" />
              <path d="M8 17h5" />
            </svg>
          </span>
          <span className="dashboard-page__action-copy">
            <span className="dashboard-page__action-title">Generate invoice</span>
            <span className="dashboard-page__action-desc">Bill clients under your business with tax invoices</span>
          </span>
        </Link>
      </div>

      <KeyContactsPanel
        contacts={eventContacts}
        contactsPath={contactsPath}
        loading={contactsQuery.isPending}
        title={data.event.artist ? `Contacts for ${data.event.artist}` : "Artist contacts"}
        emptyMessage={
          data.event.artist_id
            ? "No contacts linked to this artist yet."
            : "Link an artist on this event to surface their agents and managers here."
        }
        limit={8}
      />
      </>
      ) : null}

      {showTickets ? (
        <div className="dashboard-page__tickets-toolbar">
          <label className="dashboard-page__tickets-search">
            <span className="visually-hidden">Search tickets and walk-ins</span>
            <input
              className="eventos-input"
              placeholder="Search tiers, walk-in name or number…"
              value={ticketsSearch}
              onChange={(e) => setTicketsSearch(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="eventos-nav-btn dashboard-page__tickets-import"
            onClick={() => walkInPanelRef.current?.openImport()}
          >
            Import CSV
          </button>
        </div>
      ) : null}

      {showTickets && eventId ? (
        <div className="eventos-card dashboard-page__walk-ins-card">
          <WalkInPanel
            ref={walkInPanelRef}
            eventId={eventId}
            token={token}
            search={ticketsSearch}
            tierNames={Object.fromEntries(tierRows.map((t) => [t.tier_id, t.name]))}
            tiers={tierRows.map((t) => ({ id: t.tier_id, name: t.name }))}
          />
        </div>
      ) : null}

      {showTickets ? (
      <div className="app-shell-dash-panel">
        <div className="app-shell-dash-panel-head">
          <div>
            <h2 className="app-shell-dash-panel-title">Ticket tiers</h2>
            <span className="eventos-subtle dashboard-page__tiers-count">
              {tierRows.length} {tierRows.length === 1 ? "tier" : "tiers"}
            </span>
          </div>
          <div className="dashboard-page__tiers-actions">
            {tiersExpanded ? (
              <Link
                to={`${workspaceNav.editEvent(workspace, eventId)}#event-form-tiers`}
                className="eventos-nav-btn dashboard-page__edit-tiers-link"
              >
                Edit tiers
              </Link>
            ) : null}
            <button
              type="button"
              className="eventos-nav-btn"
              aria-expanded={tiersExpanded}
              onClick={() => setTiersExpanded((prev) => !prev)}
            >
              {tiersExpanded ? "Collapse" : "Expand"}
            </button>
          </div>
        </div>
        {tiersExpanded ? (
        <div className="app-shell-dash-panel-body">
          <div className="app-shell-table-wrap">
            <table className="app-shell-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Price</th>
                  <th>Qty</th>
                  <th>Sold</th>
                  <th>Fill</th>
                  <th className="dashboard-page__revenue-head">Revenue</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredTierRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="dashboard-page__empty-row">
                      {ticketsSearch.trim() ? "No tiers match this search." : "No ticket tiers yet."}
                    </td>
                  </tr>
                ) : (
                filteredTierRows.map((t) => {
                  const isEditing = editingTierId === t.tier_id;
                  return (
                    <tr key={t.tier_id ?? t.name}>
                      <td>
                        <strong className="dashboard-page__tier-name">{t.name}</strong>
                      </td>
                      <td>{formatINR(t.price)}</td>
                      <td>{t.quantity.toLocaleString("en-IN")}</td>
                      <td>
                        {isEditing ? (
                          <input
                            className="eventos-input"
                            type="number"
                            min={0}
                            step={1}
                            value={editingTierSoldDraft}
                            onChange={(e) => setEditingTierSoldDraft(e.target.value)}
                            style={{ width: "6.5rem" }}
                            autoFocus
                          />
                        ) : (
                          t.sold.toLocaleString("en-IN")
                        )}
                      </td>
                      <td>{formatPct(t.soldPct)}</td>
                      <td className="dashboard-page__revenue-cell">
                        {formatINR(t.revenue)}
                      </td>
                      <td>
                        {isEditing ? (
                          <div className="app-shell-table-actions">
                            <button
                              type="button"
                              className="eventos-primary-btn"
                              disabled={updateTierMutation.isPending}
                              onClick={() => submitTierEdit(t.tier_id)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="eventos-nav-btn"
                              onClick={() => {
                                setEditingTierId(null);
                                setEditingTierSoldDraft("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="eventos-nav-btn"
                            disabled={!t.tier_id}
                            onClick={() => beginTierEdit(t.tier_id, t.sold)}
                          >
                            Edit sold
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
          </div>
        </div>
        ) : null}
      </div>
      ) : null}

      {/* FE-9: Staffing model lived here AND in the Team tab (EventOSTabContent
          → workforceMetrics) — same numbers, same labels. Removed from the
          dashboard to keep "Team" as the single source of truth and remove
          a stale-data risk. */}
    </div>
  );
};

export default DashboardPage;
